import { BrowserModule } from '@angular/platform-browser';
import { APP_INITIALIZER, NgModule } from '@angular/core';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { MatDialogModule } from '@angular/material/dialog';

import { MatTabsModule } from '@angular/material/tabs';
import { AgendaComponent } from './_components/_agenda/agenda/agenda.component';

import { AppComponent } from './app.component';
import { VoxrComponent } from './_components/_interaction/voxr/voxr.component';
import { HeaderComponent } from './_components/_layout/header/header.component';
import { PartnersComponent } from './_components/_etc/partners/partners.component';
import { SafePipe } from './_shared/pipes/safe.pipe';
import { SlidoComponent } from './_components/_interaction/slido/slido.component';
import { CustomStreamComponent } from './_components/_streaming/customstream/customstream.component';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { AppRoutingModule } from './app-routing.module';
import { LoginComponent } from './_components/_auth/login/login.component';
import { KeynoteComponent } from './_components/_streaming/keynote/keynote.component';
import { MatCardModule } from '@angular/material/card';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatButtonModule } from '@angular/material/button';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { AuthInterceptor } from './_helpers/auth-interceptor';
import { NavigationComponent } from './_components/_layout/navigation/navigation.component';
import { ReceptionComponent } from './_components/_reception/reception/reception.component';
import { JitsiComponent } from './_components/_workshop/jitsi/jitsi.component';
import { MatNativeDateModule, MatRippleModule } from '@angular/material/core';
import { RegistrationFormComponent } from './_components/_registration/registration-form/registration-form.component';
import { BoothOverviewComponent } from './_components/_tradefair/booth-overview/booth-overview.component';
import { ToolbarComponent } from './_components/_layout/toolbar/toolbar.component';
import { CheckRequirementsComponent } from './_components/_techcheck/check-requirements/check-requirements.component';
import { MatIconModule } from '@angular/material/icon';
import { SchedulerComponent } from './_components/_registration/scheduler/scheduler.component';
import { RegistrationSuccessComponent } from './_components/_registration/registration-success/registration-success.component';
import { VideoAreaComponent } from './_components/_tradefair/areas/video-area/video-area.component';
import { DownloadAreaComponent } from './_components/_tradefair/areas/download-area/download-area.component';
import { BoothChatComponent } from './_components/_tradefair/areas/booth-chat/booth-chat.component';
import { YoutubeComponent } from './_components/_streaming/youtube/youtube.component';
import { TwitchComponent } from './_components/_streaming/twitch/twitch.component';
import { MentimeterComponent } from './_components/_interaction/mentimeter/mentimeter.component';
import { SocialwallComponent } from './_components/_etc/socialwall/socialwall.component';
import { BigbluebuttonComponent } from './_components/_workshop/bigbluebutton/bigbluebutton.component';
import { WorkshopOverviewComponent } from './_components/_workshop/workshop-overview/workshop-overview.component';
import { PopupComponent } from './_components/_etc/popup/popup.component';
import { NetworkingComponent } from './_components/_networking/networking.component';
import { TradefairOverviewComponent } from './_components/_tradefair/tradefair-overview/tradefair-overview.component';
import { TheoplayerComponent } from './_components/_streaming/theoplayer/theoplayer.component';
import { ExtendedTechCheckComponent } from './_components/_techcheck/extended-tech-check/extended-tech-check.component';
import { TechCheckUserValidationComponent } from './_components/_techcheck/tech-check-user-validation/tech-check-user-validation.component';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatDividerModule } from '@angular/material/divider';
import { MatSnackBarModule, MAT_SNACK_BAR_DEFAULT_OPTIONS } from '@angular/material/snack-bar';
import { ContenflowComponent } from './_components/_streaming/contenflow/contenflow.component';
import { ResizableDraggableComponent } from './_shared/components/resizable-draggable/resizable-draggable.component';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSelectModule } from '@angular/material/select';
import { OpenspaceAreaComponent } from './_components/_tradefair/areas/openspace-area/openspace-area.component';
import { SwiperModule } from 'swiper/angular';
import { HtmlAreaComponent } from './_components/_tradefair/areas/html-area/html-area.component';
import { SafeHTMLPipe } from './_shared/pipes/safe-html.pipe';
import { BbbAreaComponent } from './_components/_tradefair/areas/bbb-area/bbb-area.component';
import { PdfAreaComponent } from './_components/_tradefair/areas/pdf-area/pdf-area.component';
import { MatBottomSheetModule } from '@angular/material/bottom-sheet';
import { AgreeDataCollectionComponent } from './_components/_tradefair/tradefair-overview/agree-data-collection/agree-data-collection.component';
import { MatChipsModule, MAT_CHIPS_DEFAULT_OPTIONS } from '@angular/material/chips';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { ThreeQComponent } from './_components/_streaming/three-q/three-q.component';
import { InitAppService } from './_services/init-app.service';
import { DvgwErrorPageLoginComponent } from './_components/_auth/dvgw-error-page-login/dvgw-error-page-login.component';
import { DownloadsComponent } from './_components/_layout/navigation/downloads/downloads.component';
import { SupportComponent } from './_components/_layout/navigation/support/support.component';

import { AngularSvgIconModule } from 'angular-svg-icon';
import { AuthSupportDialogComponent } from './_components/_auth/auth-support-dialog/auth-support-dialog.component';
import { PasswordDialogComponent } from './_components/_registration/registration-form/password-dialog/password-dialog.component';
import { PopupNotificationsDialogComponent } from './_shared/components/popup-notifications-dialog/popup-notifications-dialog.component';
import { CustomPushNotifyComponent } from './_shared/components/custom-push-notify/custom-push-notify.component';
import { RetryRateLimitedRequestsInterceptor } from './_helpers/retry-rate-limited-requests.interceptor';
import { FogotPasswordDialogComponent } from './_components/_auth/fogot-password-dialog/fogot-password-dialog.component';
import { ResetPasswordComponent } from './_components/_auth/reset-password/reset-password.component';
import { LandingpageComponent } from './_components/landingpage/landingpage.component';
import { CollectionComponent } from './_components/collection/collection.component';
import { NgxExtendedPdfViewerModule } from 'ngx-extended-pdf-viewer';
export function initializeApp(initService: InitAppService): () => Promise<any> {
  return () => initService.initApp();
}
@NgModule({
  declarations: [
    AppComponent,
    VoxrComponent,
    HeaderComponent,
    PartnersComponent,
    SafePipe,
    SafeHTMLPipe,
    SlidoComponent,
    CustomStreamComponent,
    LoginComponent,
    KeynoteComponent,
    NavigationComponent,
    ReceptionComponent,
    JitsiComponent,
    RegistrationFormComponent,
    BoothOverviewComponent,
    ToolbarComponent,
    CheckRequirementsComponent,
    SchedulerComponent,
    RegistrationSuccessComponent,
    BoothChatComponent,
    DownloadAreaComponent,
    BoothChatComponent,
    VideoAreaComponent,
    YoutubeComponent,
    TwitchComponent,
    MentimeterComponent,
    SocialwallComponent,
    BigbluebuttonComponent,
    WorkshopOverviewComponent,
    PopupComponent,
    NetworkingComponent,
    TradefairOverviewComponent,
    TheoplayerComponent,
    ExtendedTechCheckComponent,
    TechCheckUserValidationComponent,
    ContenflowComponent,
    ResizableDraggableComponent,
    OpenspaceAreaComponent,
    HtmlAreaComponent,
    BbbAreaComponent,
    SafeHTMLPipe,
    AgendaComponent,
    PdfAreaComponent,
    AgreeDataCollectionComponent,
    ThreeQComponent,
    DvgwErrorPageLoginComponent,
    DownloadsComponent,
    SupportComponent,
    AuthSupportDialogComponent,
    PasswordDialogComponent,
    PopupNotificationsDialogComponent,
    CustomPushNotifyComponent,
    FogotPasswordDialogComponent,
    ResetPasswordComponent,
    LandingpageComponent,
    CollectionComponent
  ],
  imports: [
    BrowserModule,
    AppRoutingModule,
    HttpClientModule,
    BrowserAnimationsModule,
    FormsModule,
    ReactiveFormsModule,
    MatInputModule,
    MatCardModule,
    MatProgressSpinnerModule,
    MatButtonModule,
    MatFormFieldModule,
    MatCheckboxModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatRippleModule,
    MatExpansionModule,
    MatIconModule,
    MatSnackBarModule,
    MatDialogModule,
    MatToolbarModule,
    MatDividerModule,
    MatTooltipModule,
    MatSelectModule,
    SwiperModule,
    BrowserModule,
    AppRoutingModule,
    HttpClientModule,
    BrowserAnimationsModule,
    FormsModule,
    ReactiveFormsModule,
    MatInputModule,
    MatCardModule,
    MatProgressSpinnerModule,
    MatButtonModule,
    MatCheckboxModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatRippleModule,
    MatExpansionModule,
    MatIconModule,
    MatSnackBarModule,
    MatDialogModule,
    MatToolbarModule,
    MatDividerModule,
    MatTooltipModule,
    MatSelectModule,
    MatTabsModule,
    MatBottomSheetModule,
    MatChipsModule,
    MatAutocompleteModule,
    AngularSvgIconModule.forRoot(),
    NgxExtendedPdfViewerModule
  ],
  providers: [
    {
      provide: APP_INITIALIZER,
      useFactory: initializeApp,
      deps: [InitAppService],
      multi: true
    },
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: RetryRateLimitedRequestsInterceptor,
      multi: true
    },
    {
      provide: MAT_CHIPS_DEFAULT_OPTIONS,
      useValue: {
        separatorKeyCodes: [13, 188] // enter, comma
      }
    },
    { provide: MAT_SNACK_BAR_DEFAULT_OPTIONS, useValue: { horizontalPosition: 'end', verticalPosition: 'top' } }
  ],
  bootstrap: [AppComponent]
})
export class AppModule {}
